/**
 * Qué clase de fallo es, dicho una sola vez para todo el renderer.
 *
 * Existe por un defecto concreto y repetido: la pantalla trataba todos los
 * errores igual. Borrar un contacto que tiene comprobantes emitidos contestaba
 * «El servidor tuvo un problema. Intentá de nuevo en un momento» —una invitación
 * a repetir algo que **nunca** va a andar— y el diálogo quedaba abierto con el
 * botón rojo armado. La persona apretaba tres veces y llamaba a soporte.
 *
 * La distinción que hace falta es una sola y es del protocolo, no del módulo:
 *
 * - **Definitivo**: el servidor entendió el pedido y lo rechazó por lo que dice
 *   el dato o el estado del registro (4xx). Repetir el mismo gesto da el mismo
 *   resultado. Hay que decir el motivo y ofrecer la salida, no un «reintentar».
 * - **Pasajero**: el servidor se cayó, se cortó la red o hay que esperar (5xx,
 *   408, 429, o directamente no hubo respuesta). Ahí sí reintentar es lo
 *   correcto.
 *
 * Nada de esto conoce un módulo: el texto lo escribe siempre el backend.
 */

import { ApiError } from '@/lib/api';

/**
 * Estados que, siendo 4xx, sí se arreglan solos esperando: la petición estaba
 * bien y el problema es de tiempo, no del dato.
 */
const PASAJEROS_4XX = new Set([408, 425, 429]);

/**
 * `true` cuando repetir el mismo gesto va a fallar igual.
 *
 * Un error que NO es de la API —la red se cortó, el `fetch` ni salió— se toma
 * como pasajero a propósito: es el único caso donde «probá de nuevo» es un
 * consejo honesto, y equivocarse para ese lado solo cuesta un clic.
 */
export function esDefinitivo(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status >= 500) return false;
  if (PASAJEROS_4XX.has(error.status)) return false;
  return error.status >= 400;
}

/**
 * El motivo, tal como lo escribió el servidor.
 *
 * No se reescribe ni se resume: el backend redacta en castellano y nombrando el
 * dato («…hay documentos que dependen de él (Gasto, Suscripción)…»). Un «no se
 * pudo» genérico borra justamente lo que hace falta para arreglarlo.
 */
export function motivoDe(error: unknown, porDefecto = 'El servidor rechazó la operación.'): string {
  if (error instanceof Error && error.message !== '') return error.message;
  return porDefecto;
}
